/*  MUI  */
import { Button, Card, Icon } from '@auviknetworks/iceberg-ui';
import { Chip } from '@mui/material';

/* Shared */
import { PageTitle } from 'application/Shared/Components/App';
import { StringCell } from 'application/Shared/Components/Cells';
import { KebabMenu } from 'application/Shared/Components/Menu/KebabMenu';
import {
    Header,
    ManagedTableV2ContextProvider,
    ManagedTableV2UI
} from 'application/Shared/Components/Table';
import { TABLENAMES } from 'application/Shared/Constants';
import { Confirm } from 'application/Shared/Modals/NewConfirm';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { TagForm } from './TagForm';

/**
 * A table cell component that displays a chip with the tag name and color.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.row - The row data for the current cell.
 * @param {Object} props.cell - The cell data for the current cell.
 * @returns {JSX.Element} The BadgeCell component.
 */
export const BadgeCell = ({ row, cell }) => (
    <Chip
        data-testid="chip"
        label={cell.getValue()}
        size="small"
        sx={{ backgroundColor: row.original.color }}
        variant="whiteText"
    />
);

/**
 * A table cell component that displays a check icon if the cell value is truthy.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.cell - The cell data for the current cell.
 * @returns {JSX.Element|null} The CheckCell component or null if the cell value is falsy.
 */
export const CheckCell = ({ cell }) =>
    cell.getValue() && <Icon src="Check" data-testid="CheckIcon" />;

/**
 * A table cell component that displays a kebab menu with actions for the current row.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.column - The column data for the current cell.
 * @param {Object} props.row - The row data for the current cell.
 * @returns {JSX.Element} The ActionsCell component.
 */
export const ActionsCell = ({ column, row }) => (
    <KebabMenu
        options={column.columnDef.meta.options.map((option) => ({
            ...option,
            // We pass the row data to the option handler
            handler: () => option.handler(row)
        }))}
    />
);

/**
 * A component that displays a table of tags with actions to create, edit, and delete tags.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.hooks - An object containing custom hooks for interacting with tags.
 * @param {Function} props.hooks.useGetTags - A custom hook for fetching tags.
 * @param {Function} props.hooks.usePutTags - A custom hook for updating a tag.
 * @param {Function} props.hooks.usePostTags - A custom hook for creating a new tag.
 * @param {Function} props.hooks.useDeleteTags - Currently not supported! A custom hook for deleting a tag.
 * @returns {JSX.Element} The Tags component.
 */
export const Tags = ({
    hooks: { useGetTags, usePutTags, usePostTags /* useDeleteTags */ }
}) => {
    const { data, loading, refetchData } = useGetTags();
    const { putTag } = usePutTags();
    const { postTag } = usePostTags();
    // TODO: We need to review the delete proccess first.
    // const { deleteTag } = useDeleteTags();

    const [confirm, setConfirmation] = React.useState(null);
    const [edit, setEdit] = React.useState(null);
    const { t } = useTranslation();

    const columns = [
        {
            accessorKey: 'name',
            cell: BadgeCell,
            filterFn: 'arrIncludesSome',
            header: Header,
            id: 'name',
            name: 'TagEditor.tag',
            size: 160
        },
        {
            accessorKey: 'description',
            cell: StringCell,
            enableColumnFilter: false,
            enableSorting: false,
            header: Header,
            id: 'description',
            name: 'Common.description',
            size: '500'
        },
        {
            accessorKey: 'managed',
            cell: CheckCell,
            filterFn: 'equalsString',
            header: Header,
            id: 'managed',
            meta: {
                filter: {
                    control: 'Radio',
                    defaultOptions: [{ options: ['true', 'false'] }]
                }
            },
            name: 'Common.default',
            size: 160
        },
        {
            cell: ActionsCell,
            enableColumnFilter: false,
            enableSorting: false,
            header: Header,
            id: 'actions',
            meta: {
                align: 'right',
                header: { justifyContent: 'flex-end' },
                options: [
                    {
                        handler: (row) => {
                            setEdit({
                                data: row.original,
                                fields: {
                                    name: { readOnly: true }
                                },
                                reject: () => setEdit(null),
                                resolve: async (values) => {
                                    await putTag(values);
                                    refetchData();
                                },
                                title: t('Tags.modals.edit.title', row.original)
                            });
                        },
                        key: 'edit',
                        label: t('Common.edit')
                    }
                    // TODO: We need to review the delete proccess first.
                    // {
                    //     handler: (row) => {
                    //         setConfirmation({
                    //             data: row.original,
                    //             resolve: () => {
                    //                 // We delete the tag, refetch the data and close the modal
                    //                 deleteTag(row.original);
                    //                 refetchData();
                    //                 setConfirmation(null);
                    //             }
                    //         });
                    //     },
                    //     key: 'delete',
                    //     label: t('Common.delete')
                    // }
                ]
            },
            name: 'Common.actions',
            size: 50,
            testId: 'actions'
        }
    ];

    return (
        <>
            <PageTitle title={t('TagEditor.title')} />
            <TagForm
                defaultValues={edit?.data}
                fields={edit?.fields}
                isOpen={edit !== null}
                handleClose={edit?.reject}
                handleSubmit={edit?.resolve}
                title={edit?.title}
            />
            <Confirm
                isOpen={confirm !== null}
                resolve={confirm?.resolve}
                reject={() => setConfirmation(null)}
                title={t('Tags.modals.delete.title')}
                confirmButtonLabel={t('Common.delete')}
            >
                {t('Tags.modals.delete.message')}
                <ul>
                    <li className="li-with-spacing">
                        <Chip
                            label={confirm?.data.name}
                            size="small"
                            sx={{
                                backgroundColor: confirm?.data.color,
                                margin: '0 4px'
                            }}
                            variant="whiteText"
                        />
                    </li>
                </ul>
            </Confirm>
            <ManagedTableV2ContextProvider
                columns={columns}
                externalLoading={loading}
                getRowId="id"
                ignoreSearchParams
                mode="client"
                rows={data}
                rowsApiPreload
                rowsPerPage={10}
                searchEnabled
                sortKey="name"
                tableName={TABLENAMES.TAGS_COMPONENT}
            >
                <Card>
                    <ManagedTableV2UI
                        titleDef={{ titleKey: 'Tags.table.title' }}
                        renderFooter
                        bulkActions={
                            <Button
                                sx={{ marginLeft: 'auto' }}
                                onClick={() => {
                                    setEdit({
                                        reject: () => setEdit(null),
                                        resolve: async (values) => {
                                            await postTag(values);
                                            refetchData();
                                        },
                                        title: t('TagEditor.add_new_tag')
                                    });
                                }}
                            >
                                {t('TagEditor.create_new_tag')}
                            </Button>
                        }
                    />
                </Card>
            </ManagedTableV2ContextProvider>
        </>
    );
};
